import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import EnrichReviewDialog, { parseJobResult } from "../../components/EnrichReviewDialog";
import {
  anchorEntityFromEnrichmentPreview,
} from "../../utils/enrichmentReviewQueue";
import {
  enrichmentJobCardPresentation,
  formatEnrichmentJobSubtitle,
} from "../../utils/enrichmentJobCard";
import { api, apiFetch } from "../../services/api";
import { emitGuideEvent } from "../guide/guideEvents";
import { useOptionalProductGuide } from "../guide/GuideContext";
import {
  consumeInboxOpenJob,
  INBOX_OPEN_JOB_EVENT,
} from "../guide/guideStorage";
import { revealGuideAttentionTarget } from "../utils/scrollShell";
import {
  INBOX_UPLOADS_PAGE_SIZE,
  inboxUploadsApiStatus,
  inboxUploadsBoundsForOlderPage,
  inboxUploadsDateBounds,
  inboxUploadsJobTypeUrls,
  isKnowledgeIngestionJob,
  mergeInboxUploadPages,
} from "../../utils/inboxUploadsQuery";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { statusToneFor, v2cx } from "../theme/tokens";
import { useV2Theme } from "../theme/ThemeProvider";
import { useV2EnrichmentReview } from "../state/V2EnrichmentReviewContext";
import { useMobileChrome } from "../mobile/MobileChromeContext";
function formatTime(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(ts);
  }
}

function humanizeJobStatus(status, job) {
  const st = String(status || "").toLowerCase();
  if (st === "completed") {
    return String(job?.job_type || "").toLowerCase() === "knowledge_ingestion"
      ? "Indexed"
      : "Ready";
  }
  if (st === "pending") return "Queued";
  if (st === "running" || st === "processing") return "Processing";
  if (st === "failed") return "Failed";
  if (st === "cancelled") return "Cancelled";
  return status || "Unknown";
}

function parseTsMs(ts) {
  if (!ts) return null;
  const ms = new Date(ts).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function formatDurationMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/** Elapsed or total run time; for active jobs end is "now" (re-render with tick). */
function jobRunDurationParts(job, nowMs) {
  if (!job) return null;
  const st = String(job.status || "").toLowerCase();
  const terminal = ["completed", "failed", "cancelled"].includes(st);
  const startMs = parseTsMs(job.started_at || job.created_at);
  const endMs = terminal
    ? parseTsMs(job.completed_at || job.updated_at)
    : nowMs;
  if (startMs == null || endMs == null) return null;
  const dur = Math.max(0, endMs - startMs);
  if (!Number.isFinite(dur)) return null;
  return {
    label: terminal ? "Ran" : "Running",
    duration: formatDurationMs(dur),
    seconds: Math.floor(dur / 1000),
  };
}

const V2InsightsInboxPage = () => {
  const { tokens } = useV2Theme();
  const { isMobile } = useDeviceDetection();
  const { setChrome } = useMobileChrome();
  const productGuide = useOptionalProductGuide();
  const {
    enrichmentJobs,
    refreshEnrichmentJobs,
    updateEnrichmentJobPreview,
    applyClientEnrichmentCache,
    pendingReviewCount,
    isEnrichmentJobPendingReview,
  } = useV2EnrichmentReview();
  const recentJobs = enrichmentJobs;
  const guideReviewCtaRef = useRef(null);
  const guideChapterId = String(productGuide?.chapter?.id || "");
  const guideJobId = String(productGuide?.state?.jobId || "").trim();
  const guideWantsReviewCta =
    Boolean(productGuide?.active) && guideChapterId === "review";

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [reviewPreview, setReviewPreview] = useState(null);
  const [reviewEntity, setReviewEntity] = useState(null);
  const [openingJobId, setOpeningJobId] = useState(null);
  const [jobFilter, setJobFilter] = useState("all");
  const [timeRange, setTimeRange] = useState("recent");
  const [archiveJobs, setArchiveJobs] = useState([]);
  const [archiveTotal, setArchiveTotal] = useState(0);
  const [archiveHasMore, setArchiveHasMore] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveLoadingMore, setArchiveLoadingMore] = useState(false);
  const [durationTick, setDurationTick] = useState(0);
  const [reviewQueueStats, setReviewQueueStats] = useState(null);
  const [reviewSaveBusy, setReviewSaveBusy] = useState({
    locked: false,
    message: "",
    closing: false,
    requestClose: null,
  });
  const reviewSessionOpenRef = useRef(false);
  const archiveRequestIdRef = useRef(0);

  const isRecentView = timeRange === "recent";
  const jobs = isRecentView ? recentJobs : archiveJobs;

  const loadJobs = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      await refreshEnrichmentJobs({ immediate: true });
    } catch (e) {
      setError(e?.message || "Failed to load Inbox jobs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [refreshEnrichmentJobs]);

  const archiveJobsRef = useRef([]);
  useEffect(() => {
    archiveJobsRef.current = archiveJobs;
  }, [archiveJobs]);

  const archiveApiStatus = useMemo(
    () => inboxUploadsApiStatus(jobFilter),
    [jobFilter]
  );

  const fetchArchiveUploads = useCallback(
    async ({ append = false } = {}) => {
      const bounds = inboxUploadsDateBounds(timeRange);
      if (!bounds) return;
      const requestId = ++archiveRequestIdRef.current;
      if (append) setArchiveLoadingMore(true);
      else setArchiveLoading(true);
      setError("");
      try {
        const existing = append ? archiveJobsRef.current : [];
        const excludeIds = new Set(
          existing.map((j) => String(j?.id || "").trim()).filter(Boolean)
        );
        let pageBounds = bounds;
        if (append && existing.length) {
          const oldest = existing[existing.length - 1];
          pageBounds = inboxUploadsBoundsForOlderPage(bounds, oldest?.created_at);
        }
        const urls = inboxUploadsJobTypeUrls(pageBounds, {
          pageSize: INBOX_UPLOADS_PAGE_SIZE,
          status: archiveApiStatus,
          offset: 0,
        });
        const responses = await Promise.all(
          urls.map(async (url) => {
            const res = await apiFetch(url);
            if (!res?.ok) return { jobs: [], total: 0, has_more: false };
            const data = await res.json();
            return {
              jobs: Array.isArray(data?.jobs) ? data.jobs : [],
              total: Number(data?.total || 0) || 0,
              has_more: Boolean(data?.has_more),
            };
          })
        );
        if (requestId !== archiveRequestIdRef.current) return;
        const merged = mergeInboxUploadPages(
          responses,
          INBOX_UPLOADS_PAGE_SIZE,
          append ? excludeIds : null
        );
        setArchiveJobs(append ? [...existing, ...merged.jobs] : merged.jobs);
        setArchiveTotal(merged.total);
        setArchiveHasMore(merged.has_more);
      } catch (e) {
        if (requestId !== archiveRequestIdRef.current) return;
        setError(e?.message || "Failed to load older uploads");
        if (!append) {
          setArchiveJobs([]);
          setArchiveTotal(0);
          setArchiveHasMore(false);
        }
      } finally {
        if (requestId === archiveRequestIdRef.current) {
          setArchiveLoading(false);
          setArchiveLoadingMore(false);
        }
      }
    },
    [archiveApiStatus, timeRange]
  );

  useEffect(() => {
    void loadJobs(false);
  }, [loadJobs]);

  useEffect(() => {
    if (timeRange === "recent") {
      archiveRequestIdRef.current += 1;
      setArchiveJobs([]);
      setArchiveTotal(0);
      setArchiveHasMore(false);
      setArchiveLoading(false);
      setArchiveLoadingMore(false);
      return undefined;
    }
    void fetchArchiveUploads({ append: false });
    return undefined;
  }, [timeRange, archiveApiStatus, fetchArchiveUploads]);

  useEffect(() => {
    const onQueued = (ev) => {
      applyClientEnrichmentCache(ev?.detail?.jobId);
    };
    const onStop = () => {
      void loadJobs(true);
    };
    window.addEventListener("aiProcessingStop", onStop);
    window.addEventListener("newJobQueued", onQueued);
    return () => {
      window.removeEventListener("aiProcessingStop", onStop);
      window.removeEventListener("newJobQueued", onQueued);
    };
  }, [applyClientEnrichmentCache, loadJobs]);

  useEffect(() => {
    const onDatabaseChanged = () => {
      void refreshEnrichmentJobs();
      if (reviewSessionOpenRef.current) return;
      setSelectedJob(null);
      setReviewPreview(null);
      setReviewEntity(null);
      setReviewQueueStats(null);
      setError("");
    };
    window.addEventListener("database-changed", onDatabaseChanged);
    return () => window.removeEventListener("database-changed", onDatabaseChanged);
  }, [refreshEnrichmentJobs]);

  useEffect(() => {
    reviewSessionOpenRef.current = Boolean(selectedJob && reviewPreview);
  }, [selectedJob, reviewPreview]);

  const hasNonTerminalJob = useMemo(
    () =>
      jobs.some((j) =>
        !["completed", "failed", "cancelled"].includes(
          String(j?.status || "").toLowerCase()
        )
      ),
    [jobs]
  );

  useEffect(() => {
    if (!hasNonTerminalJob) return undefined;
    const id = window.setInterval(() => {
      setDurationTick((t) => t + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [hasNonTerminalJob]);

  const liveClockMs = useMemo(() => Date.now(), [durationTick, hasNonTerminalJob]);

  const summary = useMemo(() => {
    const stats = {
      total: recentJobs.length,
      pending: 0,
      running: 0,
      completed: 0,
      failed: 0,
    };
    recentJobs.forEach((j) => {
      const st = String(j?.status || "").toLowerCase();
      if (st === "completed") stats.completed += 1;
      else if (st === "failed" || st === "cancelled") stats.failed += 1;
      else if (st === "running" || st === "processing") stats.running += 1;
      else stats.pending += 1;
    });
    return stats;
  }, [recentJobs]);

  const openReviewForJob = useCallback(async (job) => {
    const id = String(job?.id || "").trim();
    if (!id) return;
    setOpeningJobId(id);
    setError("");

    const st = String(job?.status || "").toLowerCase();
    if (st !== "completed") {
      setOpeningJobId(null);
      setError("Job is not completed yet.");
      return;
    }

    const applyReviewState = (fullJob, preview) => {
      const entity =
        anchorEntityFromEnrichmentPreview(preview) || {
          id: String(preview?.anchor_application_id || "").trim(),
          name: String(preview?.anchor_name || "Application").trim() || "Application",
          type: "application",
        };
      setReviewEntity(entity);
      setReviewPreview(preview);
      setSelectedJob(fullJob);
      setOpeningJobId(null);
    };

    const cachedPreview = parseJobResult(job);
    if (cachedPreview) {
      applyReviewState(job, cachedPreview);
      void (async () => {
        try {
          const full = await api.getJobStatus(id);
          const fresh = parseJobResult(full);
          if (fresh) applyReviewState(full, fresh);
        } catch {
          /* keep cached preview */
        }
      })();
      return;
    }

    try {
      const full = await api.getJobStatus(id);
      const preview = parseJobResult(full);
      if (!preview) {
        throw new Error("No enrichment review payload found for this job.");
      }
      applyReviewState(full, preview);
    } catch (e) {
      setError(e?.message || "Unable to open job review.");
      setOpeningJobId(null);
    }
  }, []);

  // Product guide: open a specific job review (mount deep-link + live request).
  useEffect(() => {
    let cancelled = false;

    const openByJobId = async (jobId) => {
      const id = String(jobId || "").trim();
      if (!id || cancelled) return;
      try {
        await refreshEnrichmentJobs({ immediate: true });
        const full = await api.getJobStatus(id);
        if (
          !cancelled &&
          full &&
          String(full.status || "").toLowerCase() === "completed"
        ) {
          await openReviewForJob(full);
          emitGuideEvent("review-opened", { jobId: id });
        }
      } catch {
        /* ignore */
      }
    };

    const stashed = consumeInboxOpenJob();
    if (stashed) void openByJobId(stashed);

    const onRequest = (ev) => {
      // Clear any stash so a later remount doesn't open twice.
      consumeInboxOpenJob();
      const id = String(ev?.detail?.jobId || "").trim();
      if (id) void openByJobId(id);
    };
    window.addEventListener(INBOX_OPEN_JOB_EVENT, onRequest);
    return () => {
      cancelled = true;
      window.removeEventListener(INBOX_OPEN_JOB_EVENT, onRequest);
    };
  }, [openReviewForJob, refreshEnrichmentJobs]);

  const canReview = (job) => String(job?.status || "").toLowerCase() === "completed";
  const filteredJobs = useMemo(() => {
    let list = jobs;
    if (jobFilter === "to_review") {
      list = jobs.filter((j) => isEnrichmentJobPendingReview(j));
    } else if (jobFilter !== "all") {
      list = jobs.filter((j) => {
        const st = String(j?.status || "").toLowerCase();
        if (jobFilter === "completed") return st === "completed";
        if (jobFilter === "failed") return st === "failed" || st === "cancelled";
        if (jobFilter === "running") return st === "running" || st === "processing";
        if (jobFilter === "pending") {
          return (
            st !== "completed" &&
            st !== "failed" &&
            st !== "cancelled" &&
            st !== "running" &&
            st !== "processing"
          );
        }
        return true;
      });
    }
    if (jobFilter === "all") {
      return [...list].sort((a, b) => {
        const aPending = isEnrichmentJobPendingReview(a) ? 1 : 0;
        const bPending = isEnrichmentJobPendingReview(b) ? 1 : 0;
        if (bPending !== aPending) return bPending - aPending;
        const at = new Date(a?.created_at || a?.updated_at || 0).getTime();
        const bt = new Date(b?.created_at || b?.updated_at || 0).getTime();
        return bt - at;
      });
    }
    return list;
  }, [jobs, jobFilter, isEnrichmentJobPendingReview]);

  /** One job row owns the guided blinking Review CTA (prefer guide jobId). */
  const guideReviewTargetId = useMemo(() => {
    if (!guideWantsReviewCta) return "";
    if (guideJobId) {
      const match = filteredJobs.find(
        (j) =>
          String(j?.id || "") === guideJobId && isEnrichmentJobPendingReview(j)
      );
      if (match) return guideJobId;
    }
    const first = filteredJobs.find((j) => isEnrichmentJobPendingReview(j));
    return String(first?.id || "").trim();
  }, [
    filteredJobs,
    guideJobId,
    guideWantsReviewCta,
    isEnrichmentJobPendingReview,
  ]);

  const filterInitialized = useRef(false);
  useEffect(() => {
    if (loading || filterInitialized.current) return;
    filterInitialized.current = true;
    if (pendingReviewCount > 0) setJobFilter("to_review");
  }, [loading, pendingReviewCount]);

  // Guided review: prefer the to-review list so the blinking Review CTA exists.
  useEffect(() => {
    if (!guideWantsReviewCta) return;
    if (pendingReviewCount <= 0) return;
    if (selectedJob && reviewPreview) return;
    setJobFilter("to_review");
  }, [
    guideWantsReviewCta,
    pendingReviewCount,
    reviewPreview,
    selectedJob,
  ]);

  // Guided review: scroll the blinking Review button above the scrollbar fold.
  useLayoutEffect(() => {
    if (!guideWantsReviewCta) return undefined;
    if (selectedJob && reviewPreview) return undefined;
    const el = guideReviewCtaRef.current;
    if (!el) return undefined;
    let inner = 0;
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(() => {
        revealGuideAttentionTarget(el);
      });
    });
    return () => {
      window.cancelAnimationFrame(outer);
      if (inner) window.cancelAnimationFrame(inner);
    };
  }, [
    filteredJobs,
    guideReviewTargetId,
    guideWantsReviewCta,
    jobFilter,
    reviewPreview,
    selectedJob,
  ]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!selectedJob || !reviewPreview) return;
      const targetTag = String(event?.target?.tagName || "").toLowerCase();
      const isTypingTarget =
        targetTag === "input" ||
        targetTag === "textarea" ||
        targetTag === "select" ||
        Boolean(event?.target?.isContentEditable);
      if (isTypingTarget) return;

      const key = String(event.key || "").toLowerCase();
      if (key === "escape") {
        event.preventDefault();
        if (typeof reviewSaveBusy.requestClose === "function") {
          reviewSaveBusy.requestClose();
          return;
        }
        setSelectedJob(null);
        setReviewPreview(null);
        setReviewEntity(null);
        setReviewQueueStats(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [reviewPreview, selectedJob, reviewSaveBusy]);
  const handleCloseReview = useCallback(() => {
    setSelectedJob(null);
    setReviewPreview(null);
    setReviewEntity(null);
    setReviewQueueStats(null);
    setReviewSaveBusy({
      locked: false,
      message: "",
      closing: false,
      requestClose: null,
    });
    void refreshEnrichmentJobs({ immediate: true });
  }, [refreshEnrichmentJobs]);

  const requestLeaveReview = useCallback(() => {
    if (typeof reviewSaveBusy.requestClose === "function") {
      reviewSaveBusy.requestClose();
      return;
    }
    handleCloseReview();
  }, [handleCloseReview, reviewSaveBusy]);

  // Persistent chrome: root = logo+tabs; review = Map-style Back + tabs.
  useEffect(() => {
    if (!isMobile) return undefined;
    if (selectedJob && reviewPreview) {
      const title =
        enrichmentJobCardPresentation(selectedJob).title || "Review";
      setChrome({
        chromeMode: "tabs",
        contextTitle: title,
        contextSubtitle: "Review",
        onBack: requestLeaveReview,
        backLabel: "Inbox",
        headerTrailing: null,
        actionTray: null,
        hideShellHeader: false,
      });
      return undefined;
    }
    setChrome({
      chromeMode: "tabs",
      contextTitle: "Inbox",
      contextSubtitle: "",
      onBack: null,
      backLabel: "",
      headerTrailing: null,
      actionTray: null,
      hideShellHeader: false,
    });
    return undefined;
  }, [isMobile, requestLeaveReview, reviewPreview, selectedJob, setChrome]);

  const handleReviewPreviewPatch = useCallback(
    (mutator) => {
      setReviewPreview((prev) => {
        if (!prev) return prev;
        const next = mutator(prev);
        const jid = String(selectedJob?.id || "").trim();
        if (jid && next) {
          queueMicrotask(() => updateEnrichmentJobPreview(jid, next));
        }
        return next;
      });
    },
    [selectedJob?.id, updateEnrichmentJobPreview]
  );

  return (
    <section
      className={
        isMobile ? "flex min-h-0 flex-1 flex-col overflow-hidden" : undefined
      }
    >
      <div
        className={v2cx(
          isMobile
            ? "min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain"
            : selectedJob && reviewPreview
              ? "space-y-3"
              : "space-y-5"
        )}
      >
      {!(selectedJob && reviewPreview) ? (
        <h2 className="hidden text-2xl font-semibold md:block">Inbox</h2>
      ) : null}

      {selectedJob && reviewPreview ? (
        <div className="space-y-3">
          {/* One compact bar: back + job + status. Mobile: shell header owns Back/title. */}
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <button
              type="button"
              onClick={requestLeaveReview}
              className="hidden shrink-0 items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800 md:inline-flex"
            >
              <svg
                className="h-4 w-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Inbox
            </button>
            <span className="hidden h-5 w-px shrink-0 bg-slate-200 md:block" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="hidden truncate text-sm font-medium text-slate-900 md:block">
                {enrichmentJobCardPresentation(selectedJob).title}
              </p>
              <p className="truncate text-[11px] leading-tight text-slate-500 md:mt-0.5">
                {formatEnrichmentJobSubtitle(selectedJob, {
                  formatTime,
                  runParts: jobRunDurationParts(selectedJob, liveClockMs),
                })}
              </p>
            </div>
            <span
              className={v2cx(
                "shrink-0 rounded-full border px-2 py-0.5 text-[11px]",
                statusToneFor(selectedJob?.status, tokens)
              )}
            >
              {String(selectedJob?.status || "unknown")}
            </span>
            <span className="hidden shrink-0 text-[11px] text-slate-400 lg:inline">
              Esc: Back
            </span>
          </div>
          <EnrichReviewDialog
            open={true}
            inline={true}
            theme="light"
            onSaveBusyChange={setReviewSaveBusy}
            onClose={handleCloseReview}
            onQueueStatsChange={setReviewQueueStats}
            editingEntity={reviewEntity}
            preview={reviewPreview}
            onRelationshipsApplied={() => {}}
            onDescriptionApplied={() => {}}
            reviewJobId={selectedJob?.id}
            onPreviewPatch={handleReviewPreviewPatch}
          />
        </div>
      ) : (
        <>
        <section
          className={isMobile ? "space-y-2.5" : "space-y-3"}
          aria-labelledby="inbox-insights-heading"
        >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 id="inbox-insights-heading" className="text-lg font-semibold text-slate-900">
                  Your uploads
                  {pendingReviewCount > 0 ? (
                    <span className="ml-2 text-sm font-medium text-amber-700">
                      ({pendingReviewCount} need your OK)
                    </span>
                  ) : null}
                </h3>
                <p
                  className={v2cx(
                    "text-slate-600 leading-relaxed text-sm md:text-base",
                    isMobile ? "mt-0.5" : "mt-1"
                  )}
                >
                  Review extracted items before they join your map. Index-only
                  uploads show as Indexed here when they finish.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  void loadJobs(true);
                  if (timeRange !== "recent") {
                    void fetchArchiveUploads({ append: false });
                  }
                }}
                disabled={refreshing || archiveLoading}
                className={`${tokens.button.primary} disabled:opacity-50 shrink-0`}
              >
                {refreshing || archiveLoading ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            {/* Prominent review CTA — always above the fold when work awaits */}
            {pendingReviewCount > 0 ? (
              <button
                type="button"
                onClick={() => setJobFilter("to_review")}
                className={v2cx(
                  "flex w-full items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 text-left",
                  isMobile ? "px-3 py-2.5" : "px-3.5 py-3",
                  "active:bg-amber-100"
                )}
                aria-pressed={jobFilter === "to_review"}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-amber-950">
                    Needs your OK
                  </span>
                  <span className="mt-0.5 block text-xs text-amber-800/90">
                    {pendingReviewCount} upload
                    {pendingReviewCount === 1 ? "" : "s"} ready for review
                  </span>
                </span>
                <span className="inline-flex h-7 min-w-[1.75rem] shrink-0 items-center justify-center rounded-full bg-amber-500 px-2 text-xs font-bold text-white tabular-nums">
                  {pendingReviewCount}
                </span>
              </button>
            ) : null}

            {/* Mobile: one compact strip. Desktop: five summary cards. */}
            <div
              className="flex items-stretch divide-x divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white md:hidden"
              aria-label="Upload summary"
            >
              {[
                { label: "Recent", value: summary.total },
                { label: "Queued", value: summary.pending },
                { label: "Processing", value: summary.running },
                { label: "Ready", value: summary.completed },
                { label: "Failed", value: summary.failed },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5"
                >
                  <p className="truncate text-[10px] font-medium uppercase leading-none text-slate-500">
                    {stat.label}
                  </p>
                  <p className="text-base font-semibold tabular-nums leading-none text-slate-900">
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
            <div className="hidden gap-3 md:grid md:grid-cols-5">
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p className="text-[11px] uppercase text-slate-500">Recent</p>
                <p className="text-xl font-semibold mt-1">{summary.total}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p className="text-[11px] uppercase text-slate-500">Queued</p>
                <p className="text-xl font-semibold mt-1">{summary.pending}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p className="text-[11px] uppercase text-slate-500">Processing</p>
                <p className="text-xl font-semibold mt-1">{summary.running}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p className="text-[11px] uppercase text-slate-500">Ready</p>
                <p className="text-xl font-semibold mt-1">{summary.completed}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p className="text-[11px] uppercase text-slate-500">Failed</p>
                <p className="text-xl font-semibold mt-1">{summary.failed}</p>
              </div>
            </div>
            <p className="hidden text-xs text-slate-500 -mt-1 md:block">
              Summary counts reflect your latest uploads. Use the time filters
              below to browse older history.
            </p>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              <div
                className={v2cx(
                  "border-b border-slate-200 flex flex-wrap items-center gap-2",
                  isMobile ? "px-3 py-2.5" : "px-4 py-3"
                )}
              >
            {[
              ...(pendingReviewCount > 0
                ? [
                    {
                      key: "to_review",
                      label: `Needs your OK (${pendingReviewCount})`,
                      emphasize: true,
                    },
                  ]
                : []),
              {
                key: "all",
                label: isRecentView
                  ? `All (${summary.total})`
                  : `All${archiveTotal ? ` (${archiveTotal})` : ""}`,
              },
              { key: "pending", label: `Queued (${summary.pending})` },
              { key: "running", label: `Processing (${summary.running})` },
              { key: "completed", label: `Ready (${summary.completed})` },
              { key: "failed", label: `Failed (${summary.failed})` },
            ].map((chip) => {
              const active = jobFilter === chip.key;
              const emphasize = Boolean(chip.emphasize);
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setJobFilter(chip.key)}
                  className={v2cx(
                    "px-2.5 py-1 rounded-full border text-xs font-medium",
                    active
                      ? emphasize
                        ? "bg-amber-100 border-amber-400 text-amber-900"
                        : tokens.button.chipActive
                      : emphasize
                        ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                        : tokens.button.chipIdle
                  )}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          <div
            className={v2cx(
              "border-b border-slate-100 flex flex-wrap items-center gap-2 bg-slate-50/80",
              isMobile ? "px-3 py-1.5" : "px-4 py-2"
            )}
          >
            <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-1">
              When
            </span>
            {[
              { key: "recent", label: "Recent" },
              { key: "7d", label: "Last 7 days" },
              { key: "30d", label: "Last 30 days" },
              { key: "older", label: "Older than 30 days" },
            ].map((chip) => {
              const active = timeRange === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setTimeRange(chip.key)}
                  className={v2cx(
                    "px-2.5 py-1 rounded-full border text-xs font-medium",
                    active ? tokens.button.chipActive : tokens.button.chipIdle
                  )}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          {loading || (archiveLoading && !isRecentView) ? (
            <p className={v2cx("text-sm text-slate-500", isMobile ? "px-3 py-5" : "px-4 py-6")}>
              Loading your uploads…
            </p>
          ) : filteredJobs.length === 0 ? (
            <p className={v2cx("text-sm text-slate-500", isMobile ? "px-3 py-5" : "px-4 py-6")}>
              {jobFilter === "to_review"
                ? "Nothing needs your review right now."
                : isRecentView && summary.total === 0
                  ? "No uploads yet. Add knowledge from Home to get started."
                  : "No uploads match this filter."}
            </p>
          ) : (
            <>
            <ul className="divide-y divide-slate-200">
              {filteredJobs.map((job) => {
                const jid = String(job?.id || "");
                const status = String(job?.status || "unknown");
                const runParts = jobRunDurationParts(job, liveClockMs);
                const card = enrichmentJobCardPresentation(job);
                const subtitle = formatEnrichmentJobSubtitle(job, {
                  formatTime,
                  runParts,
                });
                const needsReview = isEnrichmentJobPendingReview(job);
                const indexOnly = isKnowledgeIngestionJob(job);
                const indexed =
                  indexOnly && String(status).toLowerCase() === "completed";
                const reviewed =
                  !indexOnly &&
                  String(status).toLowerCase() === "completed" &&
                  !needsReview;
                const isGuideReviewTarget =
                  Boolean(guideReviewTargetId) && jid === guideReviewTargetId;
                return (
                  <li
                    key={jid}
                    className={v2cx(
                      "flex items-center justify-between gap-3",
                      isMobile ? "px-3 py-2.5" : "px-4 py-3",
                      needsReview && "bg-amber-50/80 border-l-4 border-l-amber-400"
                    )}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p
                          className="text-sm text-slate-900 truncate font-medium"
                          title={card.title}
                        >
                          {card.title}
                        </p>
                        {needsReview ? (
                          <span className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-amber-400 bg-amber-100 text-amber-900">
                            Ready for you
                          </span>
                        ) : null}
                        {indexed ? (
                          <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800">
                            Indexed
                          </span>
                        ) : null}
                        {reviewed ? (
                          <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-500">
                            On your map
                          </span>
                        ) : null}
                      </div>
                      <p className="text-xs text-slate-500 mt-1 truncate" title={jid}>
                        {subtitle}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      <span className="text-xs px-2 py-1 rounded-full border border-slate-300 text-slate-600 tabular-nums">
                        {["running", "processing"].includes(
                          String(status).toLowerCase()
                        ) ? (
                          <span className="blink-soft">
                            Processing… {runParts ? `${runParts.seconds}s` : ""}
                          </span>
                        ) : (
                          humanizeJobStatus(status, job)
                        )}
                      </span>
                      {indexOnly ? (
                        indexed ? (
                          <span className="text-xs text-emerald-800 font-medium">
                            On Sources
                          </span>
                        ) : null
                      ) : (
                      <button
                        type="button"
                        ref={isGuideReviewTarget ? guideReviewCtaRef : undefined}
                        onClick={() => void openReviewForJob(job)}
                        disabled={!canReview(job) || openingJobId === jid}
                        className={v2cx(
                          tokens.button.success,
                          "text-xs font-medium disabled:opacity-40",
                          needsReview &&
                            "blink-soft-slow ring-2 ring-amber-400 ring-offset-1"
                        )}
                      >
                        {openingJobId === jid ? "Opening..." : "Review"}
                      </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <div
              className={v2cx(
                "border-t border-slate-200 flex flex-wrap items-center justify-between gap-2 bg-white",
                isMobile ? "px-3 py-2.5" : "px-4 py-3"
              )}
            >
              <p className="text-xs text-slate-500">
                {isRecentView
                  ? `Showing latest ${filteredJobs.length} upload${filteredJobs.length === 1 ? "" : "s"}`
                  : `Showing ${filteredJobs.length}${
                      archiveTotal ? ` of ${archiveTotal}` : ""
                    } in this time range`}
              </p>
              {!isRecentView && archiveHasMore ? (
                <button
                  type="button"
                  onClick={() => void fetchArchiveUploads({ append: true })}
                  disabled={archiveLoadingMore}
                  className={v2cx(tokens.button.secondary, "text-xs disabled:opacity-50")}
                >
                  {archiveLoadingMore ? "Loading…" : "Load more"}
                </button>
              ) : null}
              {isRecentView ? (
                <button
                  type="button"
                  onClick={() => setTimeRange("30d")}
                  className={v2cx(tokens.button.secondary, "text-xs")}
                >
                  Browse older
                </button>
              ) : null}
            </div>
            </>
          )}
            </div>
        </section>
        </>
      )}

      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      </div>
    </section>
  );
};

export default V2InsightsInboxPage;
